/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeNonNullableArray } from '@liquorice/allsorts-craftcms-nextjs';
import * as Typed from '@liquorice/allsorts-craftcms-nextjs/lib/sanitiser/sanitiserTypes';
import { EntriesFragment } from 'gql/_generated/entries.generated';
import { sanitiseAnything, SanitisedElement } from '@lib/parse/sanitiseElements';

/**
 * @example type Entry {
 *  __typename: 'page_default_Entry'
 *  sectionId: 'page',
 *  typeAlias: 'page',
 * }
 * @example type Entry {
 *  __typename: 'overview_Entry'
 *  sectionId: 'page',
 *  typeAlias: 'pageOverview',
 * }
 */

/**
 * Union of top level "Entries" defined with a '__typename'
 */
type RawEntries = EntriesFragment;

/**
 * __typename of top level Entry
 */
type EntryTypename = Typed.Typename<RawEntries>;

/**
 * Craft 5 entry type handles are global, so a `__typename` no longer reliably encodes its
 * section — e.g. the `event` section's `default` entry type is just `default_Entry`, with
 * nothing in the string identifying `event` at all. `sectionId`/`typeId` can't be parsed back
 * out of `__typename` any more, so this is an explicit, exhaustive lookup instead (the
 * `satisfies Record<EntryTypename, ...>` below makes TypeScript error here if a typename is
 * ever added to/removed from {@link EntriesFragment} without updating this table).
 */
const entryTypenameMap = {
  articleIndex_Entry: { sectionId: 'articleIndex', typeId: 'articleIndex' },
  article_default_Entry: { sectionId: 'article', typeId: 'default' },
  eventIndex_Entry: { sectionId: 'eventIndex', typeId: 'eventIndex' },
  eventPerformance_default_Entry: { sectionId: 'eventPerformance', typeId: 'default' },
  default_Entry: { sectionId: 'event', typeId: 'default' },
  ticketed_Entry: { sectionId: 'event', typeId: 'ticketed' },
  home_Entry: { sectionId: 'home', typeId: 'home' },
  learningIndex_Entry: { sectionId: 'learningIndex', typeId: 'learningIndex' },
  learning_default_Entry: { sectionId: 'learning', typeId: 'default' },
  page_default_Entry: { sectionId: 'page', typeId: 'default' },
  overview_Entry: { sectionId: 'page', typeId: 'overview' },
  profileIndex_Entry: { sectionId: 'profileIndex', typeId: 'profileIndex' },
  profile_default_Entry: { sectionId: 'profile', typeId: 'default' },
  search_Entry: { sectionId: 'search', typeId: 'search' },
  ticketIndex_Entry: { sectionId: 'ticketIndex', typeId: 'ticketIndex' },
} as const satisfies Record<EntryTypename, { sectionId: string; typeId: string }>;

type EntryTypenameMap = typeof entryTypenameMap;

/**
 * Inferred 'sectionId' from {@link EntryType}
 * Matches 'section' argument in Entry queries
 */
export type EntrySectionId<T extends EntryTypename = EntryTypename> = EntryTypenameMap[T]['sectionId'];

/**
 * All {@link EntryTypename}s belonging to a given {@link EntrySectionId}
 */
type TypenamesBySection<S extends EntrySectionId> = {
  [K in EntryTypename]: EntryTypenameMap[K]['sectionId'] extends S ? K : never;
}[EntryTypename];

/**
 * Inferred 'typeId' from {@link EntryType}
 * Matches 'type' argument in Entry queries
 */
export type EntryTypeId<T extends EntrySectionId = EntrySectionId> =
  EntryTypenameMap[TypenamesBySection<T>]['typeId'];

type EntrySectionIdToTypename<
  S extends EntrySectionId,
  U extends EntryTypeId<S> | null = null
> = Extract<
  TypenamesBySection<S>,
  {
    [K in TypenamesBySection<S>]: U extends null
      ? K
      : EntryTypenameMap[K]['typeId'] extends U
      ? K
      : never;
  }[TypenamesBySection<S>]
>;

// ----------------------------------------------------------------------------------------------------
// ---- Extracted sanitised types ----

export type SanitisedEntry<T extends EntryTypename = EntryTypename> = SanitisedElement<T>;

// ----------------------------------------------------------------------------------------------------

/**
 * Sanitise a single {@link EntriesFragment} through the {@link sanitiseAnything}`.one()` function
 * Limit the valid return Type by providing a single or array of {@link EntryTypename}
 */
export const sanitiseEntry = <
  T extends EntriesFragment,
  Name extends EntryTypename = Typed.Typename<T>
>(
  maybeEntry?: T | null,
  typeNames?: MaybeArrayOf<Name>
) => (maybeEntry ? (sanitiseAnything.one(maybeEntry, typeNames) as SanitisedEntry<Name>) : null);

/**
 * Augments the result of {@link sanitiseEntry}
 */
export const parseSanitisedEntry = <T extends EntryTypename>(
  sanitisedEntry: SanitisedEntry<T> | null
) => {
  if (!sanitisedEntry) return null;
  // const typename = sanitisedEntry.__typename as T;
  // const [sectionId, subsectionType] = typename.split('_');

  // const entryMeta = {
  //   _typeAlias: [sectionId, 'default'].includes(subsectionType) ? sectionId : `${sectionId}${subsectionType.}` ,
  //   _sectionId: sectionId,
  // }

  return { ...(sanitisedEntry as SanitisedEntry<T>) };
};

/**
 * Sanitise and parse a single  {@link EntriesFragment}
 */
export const parseEntry = <
  T extends EntriesFragment,
  Name extends EntryTypename = Typed.Typename<T>
>(
  maybeEntry?: T | null,
  typeNames?: MaybeArrayOf<Name>
) => {
  const typeNamesArr = makeNonNullableArray(typeNames);
  const sanitisedEntry = sanitiseEntry(maybeEntry, typeNamesArr.length ? typeNamesArr : undefined);
  return sanitisedEntry ? parseSanitisedEntry<Name>(sanitisedEntry) : null;
};

/**
 * Sanitise and parse multiple {@link EntriesFragment}
 */
export const parseEntries = <
  T extends EntriesFragment,
  Name extends EntryTypename = Typed.Typename<T>
>(
  maybeEntries: MaybeArrayOf<T>,
  typeNames?: MaybeArrayOf<Name>
) => {
  const rawEntriesArr = makeNonNullableArray(maybeEntries);
  const parsedEntries = rawEntriesArr.map((e) => parseEntry(e, typeNames));
  return makeNonNullableArray(parsedEntries);
};

export type Entry<
  T extends EntrySectionId = EntrySectionId,
  U extends EntryTypeId<T> | null = null
> = Exact<Typed.ExtractByTypename<ReturnType<typeof parseEntry>, EntrySectionIdToTypename<T, U>>>;

// ----------------------------------------------------------------------------------------------------
// ---- Type guards ----

interface IsEntryTypeGuard {
  (x: any): x is Entry;
  <T extends EntrySectionId>(x: any, sectionId: T): x is Entry<T>;
  <T extends EntrySectionId, U extends EntryTypeId<T> | null>(
    x: any,
    sectionId: T,
    subSection: U
  ): x is Entry<T, U>;
}

export const isEntry: IsEntryTypeGuard = <T extends EntrySectionId, U extends EntryTypeId<T> | null>(
  x: any,
  sectionId?: T,
  subSection?: U | null
): x is Entry<T, U> => {
  if (!x || typeof x !== 'object' || typeof x.__typename !== 'string') return false;

  const meta = (entryTypenameMap as Record<string, { sectionId: string; typeId: string }>)[x.__typename];
  if (!meta) return false;

  if (sectionId && sectionId !== meta.sectionId) return false;
  if (subSection && subSection !== meta.typeId) return false;

  return true;
};
